/**
 * The modal the person will see, opened over the panel from what is typed right now.
 *
 * It is the **real** `GLModal` from `gl-ui`, centred, the same component the dashboard renders —
 * so the width, the padding and what happens to a sentence that is too long are the real ones. A
 * thumbnail would agree with the dashboard everywhere except in the case worth checking.
 *
 * It reads the form, not what was saved: the point is seeing the copy before committing to it.
 * Closing it changes nothing.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { OptInTextsDraft } from '../../../Utils/optInFeatureForm';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
`;

const bodyStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

/**
 * Where the exit questionnaire goes.
 *
 * Drawn as an empty slot rather than left out: the questions are a survey, resolved at the moment
 * the modal opens and in the reader's own language, so this screen cannot render them — but a
 * preview that showed only the confirmation would teach that the modal has nothing else in it.
 */
const questionSlotStyle = css`
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  padding: var(--space-s) var(--space-m);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const missingStyle = css`
  font-style: italic;
  color: var(--color-danger);
`;

const noticeStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const Text = ({ value, fallback }: { value: string; fallback: string }): JSX.Element =>
  value.trim() !== '' ? <span>{value}</span> : <span className={missingStyle}>{fallback}</span>;

/**
 * `GLModal` takes its title and its button labels as plain strings, so a missing one cannot be
 * marked up the way the body is. It is spelled out instead of left blank, which would read as
 * "no title by design".
 */
const orMissing = (value: string, fallback: string): string =>
  value.trim() === '' ? fallback : value;

/** The preview is not operable: its buttons are there to be read, not pressed. */
const noop = (): void => undefined;

export type OptInPreviewSurface = 'confirm' | 'optOut';

export interface OptInModalPreviewProps {
  surface: OptInPreviewSurface;
  texts: OptInTextsDraft;
  /** Whether an exit questionnaire is actually being served for this feature right now. */
  hasExitQuestionnaire?: boolean;
  onClose: () => void;
}

export const OptInModalPreview = ({
  surface,
  texts,
  hasExitQuestionnaire = false,
  onClose,
}: OptInModalPreviewProps): JSX.Element => {
  if (surface === 'confirm') {
    return (
      <UTModal
        open
        position='center'
        maxWidth='460px'
        hasButtonClose
        modalContentId='opt-in-feature-preview-confirm-modal'
        closeButtonId='opt-in-feature-preview-confirm-close'
        title={orMissing(texts.confirmTitle, '(sem título)')}
        content={
          <div className={contentStyle}>
            <span className={bodyStyle}>
              <Text value={texts.confirmBody} fallback='sem texto' />
            </span>
            <span className={noticeStyle}>Preview do que está digitado agora. Nada é salvo.</span>
          </div>
        }
        customButtons={[
          { text: 'Cancelar', variant: 'secondary', onClick: onClose },
          { text: orMissing(texts.confirmCta, '(sem rótulo)'), onClick: noop },
        ]}
        onClose={onClose}
      />
    );
  }

  return (
    <UTModal
      open
      position='center'
      maxWidth='460px'
      hasButtonClose
      modalContentId='opt-in-feature-preview-optout-modal'
      closeButtonId='opt-in-feature-preview-optout-close'
      title={orMissing(texts.optOutTitle, '(sem título)')}
      content={
        <div className={contentStyle}>
          <span className={bodyStyle}>
            <Text value={texts.optOutBody} fallback='sem texto' />
          </span>

          <span id='opt-in-feature-preview-questions' className={questionSlotStyle}>
            {hasExitQuestionnaire
              ? 'As perguntas do questionário de saída entram aqui, no idioma de quem estiver saindo. Elas vivem na pesquisa, não neste cadastro — o preview delas é o do questionário.'
              : 'Sem questionário de saída aprovado, o modal para por aqui: só a confirmação, sem pergunta nenhuma.'}
          </span>

          <span className={noticeStyle}>Preview do que está digitado agora. Nada é salvo.</span>
        </div>
      }
      customButtons={[
        { text: 'Cancelar', variant: 'secondary', onClick: onClose },
        { text: orMissing(texts.optOutCta, '(sem rótulo)'), onClick: noop },
      ]}
      onClose={onClose}
    />
  );
};
